import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCheck, FaCreditCard, FaCalendarAlt, FaSync, FaStar, FaExclamationTriangle } from 'react-icons/fa';
import { api } from '../../services/api';

const SubscriptionManagement = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [renewalType, setRenewalType] = useState('automatic');
  const navigate = useNavigate();

  useEffect(() => {
    // Récupérer l'utilisateur depuis le localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
      } catch (error) {
        console.error('Erreur lors du parsing des données utilisateur:', error);
        toast.error('Erreur lors du chargement du profil utilisateur');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser?.id) return;

    const fetchSubscription = async () => {
      try {
        const response = await api.get(`/subscriptions/${currentUser.id}/subscription`);
        setSubscription(response.data);
      } catch (error) {
        console.error('Error fetching subscription:', error);
        if (error.response?.status !== 404) {
          toast.error('Erreur lors du chargement de l\'abonnement');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [currentUser?.id]);

  const handleSubscribe = async (plan) => {
    if (!currentUser?.id) {
      toast.error('Utilisateur non connecté');
      navigate('/login');
      return;
    }
    
    try {
      setProcessing(true);
      const response = await api.post(`/subscriptions/${currentUser.id}/subscribe`, { plan });
      setSubscription(response.data);
      toast.success('Abonnement souscrit avec succès!');
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la souscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      // Logique pour mettre à jour la méthode de paiement
      // Par exemple, ouvrir un modal de saisie des informations de paiement
      // ou rediriger vers une page de gestion des méthodes de paiement
      toast.info('Fonctionnalité de mise à jour du paiement à venir');
      
      // Exemple d'implémentation future :
      // const { paymentMethod } = await stripe.createPaymentMethod('card', cardElement);
      // await api.post(`/subscriptions/${currentUser.id}/payment-method`, {
      //   paymentMethodId: paymentMethod.id
      // });
      // toast.success('Méthode de paiement mise à jour avec succès');
    } catch (error) {
      console.error('Update payment method error:', error);
      toast.error('Erreur lors de la mise à jour de la méthode de paiement');
    }
  };

  const handleCancelSubscription = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler votre abonnement ?')) {
      try {
        setProcessing(true);
        await api.post(`/subscriptions/${currentUser.id}/cancel-subscription`);
        setSubscription(null);
        toast.success('Abonnement annulé avec succès');
      } catch (error) {
        console.error('Cancel subscription error:', error);
        toast.error('Erreur lors de l\'annulation de l\'abonnement');
      } finally {
        setProcessing(false);
      }
    }
  };

  const toggleRenewalType = async () => {
    const newRenewalType = renewalType === 'automatic' ? 'manual' : 'automatic';
    setRenewalType(newRenewalType);
    
    try {
      // Mettre à jour le type de renouvellement dans l'API
      await api.put(`/subscriptions/${currentUser?.id}/renewal-type`, {
        renewalType: newRenewalType
      });
      
      // Mettre à jour l'état local de l'abonnement
      setSubscription(prev => ({
        ...prev,
        renewal_type: newRenewalType
      }));
      
      toast.success(`Renouvellement ${newRenewalType === 'automatic' ? 'automatique' : 'manuel'} activé avec succès`);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du type de renouvellement:', error);
      toast.error('Erreur lors de la mise à jour du type de renouvellement');
      // Revert to previous state on error
      setRenewalType(renewalType);
    }
  };

  const subscriptionPlans = [
    {
      id: 'basic',
      name: 'Basique',
      price: '9.99',
      currency: '€',
      period: 'mois',
      features: [
        '5 annonces par mois',
        'Support par email',
        'Accès de base aux fonctionnalités'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '19.99',
      currency: '€',
      period: 'mois',
      features: [
        'Annonces illimitées',
        'Support prioritaire',
        'Profil mis en avant',
        'Statistiques avancées',
        'Assistance téléphonique'
      ],
      popular: true
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center my-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="flex flex-col items-center justify-center py-12">
          <FaExclamationTriangle className="text-yellow-500 text-5xl mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Accès non autorisé</h2>
          <p className="text-gray-600 mb-6 text-center">
            Vous devez être connecté pour accéder à cette page.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Gestion de l'abonnement</h1>
      
      {subscription ? (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <div className="mb-4 md:mb-0">
                <h2 className="text-xl font-semibold">Votre abonnement actuel : {subscription.planName || 'Pro'}</h2>
                <p className="text-gray-600">Prochain paiement : {new Date(subscription.nextBillingDate).toLocaleDateString()}</p>
              </div>
              <button 
                onClick={handleCancelSubscription}
                disabled={processing}
                className={`px-4 py-2 rounded-md ${processing ? 'bg-gray-400' : 'bg-red-500 hover:bg-red-600'} text-white font-medium`}
              >
                {processing ? 'Traitement...' : 'Annuler l\'abonnement'}
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <FaCreditCard className="text-blue-500 mr-2" />
                  Détails de l'abonnement
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Méthode de paiement</p>
                      <p className="text-gray-600 text-sm">{subscription.paymentMethod || 'Carte se terminant par ****1234'}</p>
                    </div>
                    <button 
                      onClick={handleUpdatePaymentMethod}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <FaSync className="mr-1" /> Mettre à jour
                    </button>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium flex items-center">
                      <FaCalendarAlt className="text-blue-500 mr-2" />
                      Date de renouvellement
                    </p>
                    <p className="text-gray-600 text-sm mt-1">
                      {new Date(subscription.nextBillingDate).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium flex items-center">
                          <FaSync className="text-blue-500 mr-2" />
                          Type de renouvellement
                        </p>
                        <p className="text-gray-600 text-sm mt-1">
                          {renewalType === 'automatic' 
                            ? 'Renouvellement automatique' 
                            : 'Renouvellement manuel'}
                        </p>
                      </div>
                      <button 
                        type="button"
                        onClick={toggleRenewalType}
                        disabled={processing}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${renewalType === 'automatic' ? 'bg-blue-500' : 'bg-gray-300'}`}
                      >
                        <span className="sr-only">Changer le type de renouvellement</span>
                        <span 
                          className={`${renewalType === 'automatic' ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {renewalType === 'automatic'
                        ? 'Votre abonnement sera renouvelé automatiquement.'
                        : 'Vous devrez renouveler manuellement votre abonnement.'}
                    </p>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">Statut</p>
                    <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium ${
                      subscription.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscription.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-4">Avantages de votre abonnement</h3>
                <ul className="space-y-3">
                  {subscriptionPlans
                    .find(p => p.id === (subscription.planId || 'pro'))
                    ?.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="text-xl font-semibold mb-6">Choisissez un forfait</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subscriptionPlans.map((plan) => (
              <div 
                key={plan.id}
                className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                  plan.popular ? 'border-blue-500' : 'border-transparent'
                } relative`}
              >
                {plan.popular && (
                  <div className="absolute top-4 -right-10 bg-blue-500 text-white text-xs font-bold px-10 py-1 transform rotate-45">
                    POPULAIRE
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                    {plan.popular && <FaStar className="text-yellow-400" />}
                  </div>
                  
                  <div className="mb-6">
                    <span className="text-3xl font-bold">{plan.price}{plan.currency}</span>
                    <span className="text-gray-500"> / {plan.period}</span>
                  </div>
                  
                  <div className="h-px bg-gray-200 my-4"></div>
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={processing}
                    className={`w-full py-2 px-4 rounded-md font-medium ${
                      plan.popular 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-white border border-blue-600 text-blue-600 hover:bg-blue-50'
                    } ${processing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {processing ? 'Traitement...' : 'Choisir ce forfait'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">Historique des paiements</h3>
          <p className="text-gray-500 text-sm">Consultez l'historique de vos paiements</p>
        </div>
        <div className="p-6">
          <p className="text-gray-500">
            {subscription 
              ? 'Aucun historique de paiement pour le moment.'
              : 'Activez un abonnement pour voir l\'historique des paiements.'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export { SubscriptionManagement as default };
